/**
 * Two locales meet in a date. The app's language names the months and the
 * weekdays; the browser's locale (the format locale) decides the field order
 * and the first day of the week. A user in the US on a German browser reads
 * English names, types DD/MM/YYYY and sees Monday first.
 */
import type { IsoDate, Weekday } from "./calendarMath";
import { utcDateOf } from "./calendarMath";

/** Month first (US) or day first (everyone else). */
export type IDateOrder = "mdy" | "dmy";

export type IDateFormats = {
  /** "October 2026": the days grid's name. */
  monthYear: Intl.DateTimeFormat;
  /** "October": the header and the months view. */
  month: Intl.DateTimeFormat;
  /** "Wednesday, October 7, 2026": a day's accessible name. */
  full: Intl.DateTimeFormat;
  /** "October 7, 2026" (US), "7 October 2026" (GB): dates shown as text. */
  long: Intl.DateTimeFormat;
  /** "Oct 7, 2026" (US), "7 Oct 2026" (GB). */
  medium: Intl.DateTimeFormat;
  weekdayShort: Intl.DateTimeFormat;
  weekdayLong: Intl.DateTimeFormat;
};

export type IDateLocale = {
  /** The browser's tag (or the app's override): order and week start. */
  formatLocale: string;
  /** The language with the format locale's region (`en-DE`): every name. */
  displayLocale: string;
  firstDay: Weekday;
  order: IDateOrder;
  /** "MM/DD/YYYY" or "DD/MM/YYYY": the mask and the placeholder. */
  pattern: string;
  formats: IDateFormats;
};

const FALLBACK_LOCALE = "en-US";

/** The browser's preferred tag; `en-US` where there is none (SSR, tests). */
export function browserLocale(): string {
  if (typeof navigator === "undefined") return FALLBACK_LOCALE;
  return navigator.languages?.[0] ?? navigator.language ?? FALLBACK_LOCALE;
}

type WeekInfo = { firstDay: Weekday };

/** Older engines lack `getWeekInfo()`; some expose only the `weekInfo` accessor. */
type WeekLocale = Omit<Intl.Locale, "getWeekInfo"> & {
  getWeekInfo?(): WeekInfo;
  weekInfo?: WeekInfo;
};

/** CLDR's Sunday-first regions, for engines without week info. */
const SUNDAY_FIRST = new Set(
  (
    "AG AS BD BR BS BT BW BZ CA CN CO DM DO ET GT GU HK HN ID IL IN JM JP KE " +
    "KH KR LA MH MM MO MT MX MZ NI NP PA PE PH PK PR PT PY SA SG SV TH TT TW " +
    "UM US VE VI WS YE ZA ZW"
  ).split(" "),
);

function regionOf(locale: string): string | undefined {
  try {
    return new Intl.Locale(locale).maximize().region;
  } catch {
    return undefined;
  }
}

/**
 * The locale's first day of the week: `getWeekInfo()`, else the older
 * `weekInfo` accessor, else CLDR's table by region.
 */
export function firstDayOfWeek(locale: string): Weekday {
  try {
    const info = new Intl.Locale(locale) as WeekLocale;
    const week = info.getWeekInfo?.() ?? info.weekInfo;
    if (week) return week.firstDay;
  } catch {
    return 7;
  }
  const region = regionOf(locale);
  return region === undefined || SUNDAY_FIRST.has(region) ? 7 : 1;
}

/** A day and a month past 12, so the sample shows which part is which. */
const SAMPLE = Date.UTC(2026, 8, 24);

/**
 * Month first only where the locale writes month, day, year (the US and its
 * neighbours); year-first locales (sv, lt, hu) take the European day first.
 */
export function dateOrder(locale: string): IDateOrder {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "UTC",
    })
      .formatToParts(SAMPLE)
      .map((part) => part.type)
      .filter((type) => type === "day" || type === "month" || type === "year");
    return parts.join() === "month,day,year" ? "mdy" : "dmy";
  } catch {
    return "mdy";
  }
}

export function datePattern(order: IDateOrder): string {
  return order === "mdy" ? "MM/DD/YYYY" : "DD/MM/YYYY";
}

/** The language with the format locale's region: `en` + `de-DE` = `en-DE`. */
export function displayLocaleOf(
  language: string,
  formatLocale: string,
): string {
  const region = regionOf(formatLocale);
  try {
    return new Intl.Locale(language, region ? { region } : {}).toString();
  } catch {
    return language;
  }
}

function formatsOf(locale: string): IDateFormats {
  // UTC both ways: a day is built at UTC midnight (`utcDateOf`).
  const make = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, { timeZone: "UTC", ...options });
  return {
    monthYear: make({ month: "long", year: "numeric" }),
    month: make({ month: "long" }),
    full: make({ dateStyle: "full" }),
    long: make({ dateStyle: "long" }),
    medium: make({ dateStyle: "medium" }),
    weekdayShort: make({ weekday: "short" }),
    weekdayLong: make({ weekday: "long" }),
  };
}

// Formatters are costly to build: one set per pair, kept for the session.
const cache = new Map<string, IDateLocale>();

export function dateLocaleOf(
  language: string,
  formatLocale: string,
): IDateLocale {
  const key = `${language}|${formatLocale}`;
  let locale = cache.get(key);
  if (!locale) {
    const displayLocale = displayLocaleOf(language, formatLocale);
    const order = dateOrder(formatLocale);
    locale = {
      formatLocale,
      displayLocale,
      firstDay: firstDayOfWeek(formatLocale),
      order,
      pattern: datePattern(order),
      formats: formatsOf(displayLocale),
    };
    cache.set(key, locale);
  }
  return locale;
}

/** A day as text by one of the locale's formats. */
export function formatIsoDate(
  date: IsoDate,
  format: Intl.DateTimeFormat,
): string {
  return format.format(utcDateOf(date));
}
